// Helpers
import { transformDateToLocalDateString, transformRoute } from "helpers";

// Servisni sloj
import { FleetVehicleDriverRow } from "services/api/fleet/vehicles/drivers/types";

// UI
import Badge from "ui/components/Badge/Badge";
import { ButtonLink } from "ui/components/Button/ButtonLink";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";

// Navigacija
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { Routes as UserRoutes } from "ui/modules/users/navigation";

/** Inicijali vozača za ikonu — vozač je osoba, pa dvije riječi daju dva slova. */
export const getDriverInitials = (name: string | null): string =>
  (name ?? "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("") || "?";

/**
 * Rok isprave kao badge — zelen dok vrijedi, crven kad je prošao. Bez datuma nema što
 * prikazati, pa se redak izostavlja umjesto da stoji prazna oznaka.
 */
const renderExpiry = (expiry: Date | null | undefined) => {
  if (!expiry) return null;

  const date = new Date(expiry);
  const isExpired = date.getTime() < Date.now();

  return (
    <Badge
      text={transformDateToLocalDateString(date) ?? ""}
      variant={isExpired ? "danger" : "success"}
      height="small"
      showDot
    />
  );
};

/**
 * Broj isprave uz rok — isti raspored za vozačku, osobnu i putovnicu. Prikazuje se samo
 * ono što je uneseno; kad isprave nema, ostaje crtica da ćelija ne bude prazna.
 */
export const renderDriverDocument = (
  documentNumber: string | null,
  expiry: Date | null | undefined
) => {
  const number = documentNumber?.trim() || null;
  const expiryBadge = renderExpiry(expiry);

  if (!number && !expiryBadge) {
    return <span className="text-sm text-slate-400">-</span>;
  }

  // Broj i rok stoje u istom retku; na uskoj koloni se rok prelomi ispod broja.
  return (
    <div className="flex flex-wrap items-center gap-2">
      {number && <span className="text-sm text-slate-900">{number}</span>}
      {expiryBadge}
    </div>
  );
};

export const FLEET_VEHICLE_DRIVER_SEARCH_COLUMNS = (
  t: (key: string) => string,
  onVehicleClick?: (event: React.MouseEvent, vehicleId: string) => void,
  onDeleteClick?: (event: React.MouseEvent<HTMLDivElement>, id: string) => void
): Column[] => [
  {
    title: "",
    key: "driver_icon",
    alwaysVisible: true,
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDriverRow) => (
      <TableIcon
        imageUrl={row.contact?.pictures?.[0]?.url ?? null}
        initials={getDriverInitials(row.name)}
      />
    ),
  },
  {
    title: "admin.fleet.vehicle.driver.search.contact",
    key: "driver_name",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDriverRow) => (
      <div>
        <div className="text-md font-medium text-slate-900 truncate">
          {row.name ?? "-"}
        </div>
        {/* Iza vozača je korisnik aplikacije ili kontakt iz imenika. */}
        <div className="text-xs text-slate-500">
          {row.userId ? "Korisnik aplikacije" : "Kontakt"}
        </div>
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.driver.search.vehicle",
    key: "driver_vehicle",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDriverRow) => {
      if (!row.vehicle) return null;

      return (
        <VehicleCell
          vehicle={row.vehicle}
          onClick={(e) => onVehicleClick?.(e, row.vehicle.id!)}
        />
      );
    },
  },
  {
    title: "admin.fleet.vehicle.driver.search.driverslicence",
    key: "driver_licence",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDriverRow) =>
      renderDriverDocument(row.driversLicenceNumber, row.driversLicenceExpiry),
  },
  {
    title: "admin.fleet.vehicle.driver.search.identitycard",
    key: "driver_identity_card",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDriverRow) =>
      renderDriverDocument(row.identityCardNumber, row.identityCardExpiry),
  },
  {
    title: "admin.fleet.vehicle.driver.search.passport",
    key: "driver_passport",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDriverRow) =>
      renderDriverDocument(row.passportNumber, row.passportExpiry),
  },
  {
    title: "",
    key: "driver_actions",
    field: "",
    type: ColumnTypes.custom,
    // Vozač je kontakt ili korisnik aplikacije, pa akcija vodi na karticu onoga
    // što stvarno stoji iza njega.
    render: (row: FleetVehicleDriverRow) => (
      <ButtonMenu endIcon="/media/icons/more-dots.svg" variant="text">
        {row.contact ? (
          <ButtonLink
            startIcon="/media/icons/contact.svg"
            cssClassName="text-slate-600 hover:bg-blue-100"
            size="full"
            variant="text"
            to={transformRoute(ContactRoutes.ContactDetailPageRouter, {
              id: row.contact.id,
            })}
          >
            {t("admin.fleet.vehicle.search.contact.detail")}
          </ButtonLink>
        ) : (
          <ButtonLink
            startIcon="/media/icons/users.svg"
            cssClassName="text-slate-600 hover:bg-blue-100"
            size="full"
            variant="text"
            to={transformRoute(UserRoutes.UserDetailPageRouter, {
              id: row.userId ?? "",
            })}
          >
            Detalji korisnika
          </ButtonLink>
        )}

        <ButtonMenuItem
          startIcon="/media/icons/trash.svg"
          variant="remove"
          className="text-slate-600 hover:bg-blue-100"
          onClick={(e) => onDeleteClick?.(e, row.id)}
        >
          Obriši
        </ButtonMenuItem>
      </ButtonMenu>
    ),
  },
];
